import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUsuarioSistemaId, getAuthToken, isLoggedIn, getUsuarioLogado } from '../utils/auth';
import './MinhaConta.css';// IDs de tipos de usuário que podem editar foto de perfil
import BASE_URL from '../utils/api';
const TIPOS_PERMITIDOS_FOTO = [5, 6, 3]; // CEO, Gerente de TI, Gerente

export default function MinhaConta() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadingFoto, setUploadingFoto] = useState(false);
  const fileInputRef = useRef(null);
  
  // Foto de perfil
  const [fotoPerfil, setFotoPerfil] = useState(null);
  
  // Dados do UsuarioSistema
  const [usuarioSistema, setUsuarioSistema] = useState({
    id: null,
    login: '',
    tipoPessoa: '',
    cpfUsuario: '',
    cnpjUsuario: '',
    tipoUsuarioLoginId: null
  });
  
  // Dados do DadosUsuario
  const [dadosUsuario, setDadosUsuario] = useState({
    id: null,
    nomeUsuario: '',
    email: '',
    telefone: '',
    usuarioSistemaId: null
  });
  
  // Verificar se pode editar foto
  const podeEditarFoto = TIPOS_PERMITIDOS_FOTO.includes(usuarioSistema.tipoUsuarioLoginId);
  
  // Obter inicial do nome
  const getInitial = (name) => {
    if (!name) return '?';
    const clean = name.replace(/[^a-zA-ZÀ-ÿ]/g, '');
    return clean.charAt(0).toUpperCase() || '?';
  };

  useEffect(() => {
    if (!isLoggedIn()) {
      navigate('/sidebar/Login');
      return;
    }
    carregarDados();
  }, [navigate]);

  const carregarDados = async () => {
    try {
      setLoading(true);
      setError('');
      
      const userInfo = getUsuarioLogado();
      if (!userInfo || !userInfo.login) {
        setError('Usuário não identificado. Faça login novamente.');
        return;
      }

      // Buscar UsuarioSistema por login
      const resUsuario = await fetch(`${BASE_URL}/UsuarioSistema/por-login/${userInfo.login}`, {
        headers: { 'Authorization': 'Bearer ' + (getAuthToken() || '') }
      });
      if (!resUsuario.ok) {
        throw new Error('Erro ao carregar dados do usuário');
      }
      const dataUsuario = await resUsuario.json();
      console.log('Dados carregados:', dataUsuario); // Debug
      setUsuarioSistema({
        id: dataUsuario.id || null,
        login: dataUsuario.login || '',
        tipoPessoa: dataUsuario.tipoPessoa || '',
        cpfUsuario: dataUsuario.cpfUsuario || '',
        cnpjUsuario: dataUsuario.cnpjUsuario || '',
        tipoUsuarioLoginId: dataUsuario.tipoUsuarioLoginId || null
      });

      // Buscar DadosUsuario usando o ID do UsuarioSistema
      const resDados = await fetch(`${BASE_URL}/DadosUsuario/por-usuario/${dataUsuario.id}`, {
        headers: { 'Authorization': 'Bearer ' + (getAuthToken() || '') }
      });
      if (resDados.ok) {
        const dataDados = await resDados.json();
        setDadosUsuario({
          id: dataDados.id || null,
          nomeUsuario: dataDados.nomeUsuario || '',
          email: dataDados.email || '',
          telefone: dataDados.telefone || '',
          usuarioSistemaId: dataDados.usuarioSistemaId || dataUsuario.id
        });
        
        // Carregar foto de perfil via endpoint específico
        if (dataDados.id) {
          try {
            const token = getAuthToken();
            const fotoRes = await fetch(`${BASE_URL}/DadosUsuario/${dataDados.id}/foto`, {
              headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            });
            
            if (fotoRes.ok) {
              const blob = await fotoRes.blob();
              if (blob.size > 0) {
                const reader = new FileReader();
                reader.onload = () => {
                  setFotoPerfil(reader.result);
                };
                reader.readAsDataURL(blob);
              } else {
                // Blob vazio = sem foto
                setFotoPerfil(null);
              }
            } else {
              // Endpoint retornou erro = sem foto
              setFotoPerfil(null);
            }
          } catch (fotoErr) {
            // Erro ao carregar foto = deixa null
            console.log('Sem foto de perfil cadastrada');
            setFotoPerfil(null);
          }
        }
      } else {
        console.log('DadosUsuario não encontrados (ainda não cadastrados)');
      }

    } catch (err) {
      setError('Erro ao carregar dados: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUsuarioChange = (e) => {
    const { name, value } = e.target;
    setUsuarioSistema(prev => ({ ...prev, [name]: value }));
  };

  const handleDadosChange = (e) => {
    const { name, value } = e.target;
    setDadosUsuario(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      const token = getAuthToken();
      const headers = {
        'Content-Type': 'application/json'
      };
      
      // Adicionar token se disponível
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Apenas atualizar DadosUsuario (nome, email, telefone)
      // UsuarioSistema (login, CPF, CNPJ) são dados sensíveis que não devem ser editados pelo usuário
      if (dadosUsuario.id) {
        const dadosPayload = {
          id: dadosUsuario.id,
          nomeUsuario: dadosUsuario.nomeUsuario,
          email: dadosUsuario.email,
          telefone: dadosUsuario.telefone,
          usuarioSistemaId: dadosUsuario.usuarioSistemaId
        };

        const resDados = await fetch(`${BASE_URL}/DadosUsuario/${dadosUsuario.id}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(dadosPayload)
        });

        if (!resDados.ok) {
          const text = await resDados.text();
          throw new Error(text || 'Erro ao atualizar dados do usuário');
        }
        
        setSuccess('Dados atualizados com sucesso!');
      } else {
        // Se não existe DadosUsuario, criar um novo
        const dadosPayload = {
          nomeUsuario: dadosUsuario.nomeUsuario,
          email: dadosUsuario.email,
          telefone: dadosUsuario.telefone,
          usuarioSistemaId: usuarioSistema.id
        };

        const resDados = await fetch(`${BASE_URL}/DadosUsuario`, {
          method: 'POST',
          headers,
          body: JSON.stringify(dadosPayload)
        });

        if (!resDados.ok) {
          const text = await resDados.text();
          throw new Error(text || 'Erro ao criar dados do usuário');
        }
        
        const novoDados = await resDados.json();
        setDadosUsuario(prev => ({ ...prev, id: novoDados.id }));
        setSuccess('Dados salvos com sucesso!');
      }
      
      // Atualizar localStorage com novos dados
      const userInfo = {
        id: usuarioSistema.id,
        login: usuarioSistema.login,
        tipoUsuarioLoginId: usuarioSistema.tipoUsuarioLoginId,
        tipoPessoa: usuarioSistema.tipoPessoa,
        cpfUsuario: usuarioSistema.cpfUsuario,
        cnpjUsuario: usuarioSistema.cnpjUsuario
      };
      localStorage.setItem('userInfo', JSON.stringify(userInfo));

    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Handler para upload de foto de perfil
  const handleFotoClick = () => {
    if (podeEditarFoto && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Verificar se é imagem
    if (!file.type.startsWith('image/')) {
      setError('Por favor, selecione uma imagem válida.');
      return;
    }
    
    // Verificar tamanho (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('A imagem deve ter no máximo 5MB.');
      return;
    }
    
    setUploadingFoto(true);
    setError('');
    
    try {
      // Enviar para o backend usando multipart/form-data
      if (!dadosUsuario.id) {
        setError('Salve seus dados pessoais primeiro antes de adicionar foto.');
        setUploadingFoto(false);
        return;
      }
      
      const formData = new FormData();
      formData.append('foto', file);
      
      const token = getAuthToken();
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      const res = await fetch(`${BASE_URL}/DadosUsuario/${dadosUsuario.id}/upload-foto`, {
        method: 'POST',
        headers,
        body: formData
      });
      
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(errorText || 'Erro ao salvar foto de perfil');
      }
      
      setSuccess('Foto de perfil atualizada com sucesso!');
      setUploadingFoto(false);
      
      // Recarregar dados para obter a foto do backend
      await carregarDados();
    } catch (err) {
      setError('Erro ao fazer upload da foto: ' + err.message);
      setUploadingFoto(false);
    }
  };

  if (loading) {
    return (
      <div className="minha-conta-container">
        <div className="minha-conta-card">
          <p>Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="minha-conta-container" style={{ position: 'relative' }}>
      <button
        onClick={() => navigate('/sidebar/Configuracoes')}
        className="planos-btn-voltar"
        style={{ position: 'absolute', top: 10, left: 10, zIndex: 20 }}
        title="Voltar para Configurações"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        Voltar
      </button>
      <div className="minha-conta-card">
        <div className="minha-conta-header">
          {/* Input hidden para upload de foto */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFotoChange}
            accept="image/*"
            style={{ display: 'none' }}
          />
          
          <div className="minha-conta-avatar-wrapper">
            {podeEditarFoto ? (
              /* CEO, Gerente ou Gerente de TI - Pode editar foto */
              <div className="minha-conta-avatar-edit-container">
                <div className="minha-conta-avatar-circle">
                  {fotoPerfil ? (
                    <img src={fotoPerfil} alt="Foto de perfil" className="minha-conta-avatar-img" />
                  ) : (
                    <span className="minha-conta-avatar-initial">
                      {getInitial(dadosUsuario.nomeUsuario)}
                    </span>
                  )}
                  {uploadingFoto && (
                    <div className="minha-conta-avatar-loading">
                      <span>...</span>
                    </div>
                  )}
                </div>
                <div className="minha-conta-edit-area">
                  <button
                    type="button"
                    className="minha-conta-edit-foto-btn"
                    onClick={handleFotoClick}
                    disabled={uploadingFoto}
                    aria-label="Editar foto de perfil"
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill="currentColor"/>
                    </svg>
                  </button>
                </div>
              </div>
            ) : (
              /* Funcionário - Apenas mostra ícone padrão */
              <div className="minha-conta-avatar">
                <svg width="210" height="210" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="11" fill="#f5f5f5" stroke="rgb(235, 133, 0)" strokeWidth="1.5"/>
                  <circle cx="12" cy="9" r="3.5" fill="rgb(235, 133, 0)"/>
                  <path d="M5.5 19.5c0-3.5 3-5.5 6.5-5.5s6.5 2 6.5 5.5" stroke="rgb(235, 133, 0)" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
            )}
          </div>
          <h2>Minha Conta</h2>
          <p>Gerencie suas informações pessoais</p>
        </div>

        {error && <div className="minha-conta-error">{error}</div>}
        {success && <div className="minha-conta-success">{success}</div>}

        <form onSubmit={handleSubmit} className="minha-conta-form">
          <div className="form-section">
            <h3>Dados de Acesso</h3>
            
            <div className="form-group">
              <label>Login</label>
              <input
                type="text"
                name="login"
                value={usuarioSistema.login}
                disabled
                className="input-disabled"
              />
            </div>

            {(usuarioSistema.cpfUsuario || usuarioSistema.cnpjUsuario) && (
              <div className="form-group">
                <label>{usuarioSistema.cpfUsuario ? 'CPF' : 'CNPJ'}</label>
                <input
                  type="text"
                  value={
                    usuarioSistema.cpfUsuario
                      ? usuarioSistema.cpfUsuario.substring(0, 2) + '*******' + usuarioSistema.cpfUsuario.substring(usuarioSistema.cpfUsuario.length - 2)
                      : usuarioSistema.cnpjUsuario
                      ? usuarioSistema.cnpjUsuario.substring(0, 3) + '*********-' + usuarioSistema.cnpjUsuario.substring(usuarioSistema.cnpjUsuario.length - 2)
                      : ''
                  }
                  disabled
                  className="input-disabled"
                />
              </div>
            )}
          </div>

          <div className="form-section">
            <h3>Dados Pessoais</h3>
            
            <div className="form-group">
              <label>Nome Completo</label>
              <input
                type="text"
                name="nomeUsuario"
                value={dadosUsuario.nomeUsuario}
                onChange={handleDadosChange}
                placeholder="Seu nome completo"
              />
            </div>

            <div className="form-group">
              <label>E-mail</label>
              <input
                type="email"
                name="email"
                value={dadosUsuario.email}
                onChange={handleDadosChange}
                placeholder="seu@email.com"
              />
            </div>

            <div className="form-group">
              <label>Telefone</label>
              <input
                type="tel"
                name="telefone"
                value={dadosUsuario.telefone}
                onChange={handleDadosChange}
                placeholder="(00) 00000-0000"
              />
            </div>
          </div>

          <button type="submit" className="btn-atualizar" disabled={saving}>
            {saving ? 'Atualizando...' : 'Atualizar Dados'}
          </button>
        </form>
      </div>
    </div>
  );
}
